import * as React from 'react';

import {
    Accordion, AccordionTab, BreadcrumbItem, Chip, F,
    useDataTable, useDebounce, useForm, useLoading, usePermissions, useSearch, useStringState, useTranslation,
    useWorkFormSettings,
    ValidationBuilder
} from '@components';
import {
    IJob, IWork, IContractor, IPropertyGroup, PropertyGroupObjectType, IPropertyGroupType,
    IJobHasContractor,
    ISecurity,
    ISubContractorRequest, ISubMainContractorRequest
} from '@models';
import { DynamicProperties } from '../user/DynamicProperties';
import dateUtils from '@utils/date-utils';
import { ModuleManager } from '@modules';
import security from '@utils/security';

export interface IProps {
    job: IJob;
    modules: ModuleManager;
    parentContractor: IJobHasContractor;
    propertyGroupTypes: IPropertyGroupType[];
    onCancel: Function;
    onSuccess: Function;
    saveJobHasContractor: Function;
    saveSubContractorRequest: Function;
    saveSubMainContractorRequest: Function;
    searchContractors: Function;
    security: ISecurity;
    work: IWork;
    getJobContractors: Function;
    getContractorFromAppUser: Function;
    main: boolean;
}

export const validateContractor = (data: Partial<IJobHasContractor>, work: IWork) => {
    return ValidationBuilder.create(data)
        .notEmptyIf(work.contractorSettings?.jhcStartDateMandatory == true,'startDate', 'Date is required')
        .notEmptyIf(work.contractorSettings?.jhcDescriptionMandatory == true,'jobsDescription')
        .build();
}

const isValid = (data: Partial<IJobHasContractor>, work: IWork) => {
    return Object.keys(validateContractor(data, work)).length == 0;
}

function JobAddContractorImpl(props: IProps) {
    const { t } = useTranslation();
    const currentSearchTerm = useStringState();
    const loading = useLoading(false);

    const [activeIndex, setActiveIndex] = React.useState<number>(0);
    const [contractors, setContractors] = React.useState<IContractor[]>([]);
    const [selectedContractor, setSelectedContractor] = React.useState<IContractor|undefined>();
    const debouncedSearchTerm = useDebounce(currentSearchTerm.value, 500);


    const doSearch = loading.wrap(async (term: string | undefined) => {
        const selfContractors = await props.getContractorFromAppUser(props.work.id);
        let data = await props.searchContractors(props.work.id, term ?? '');
        const jobContractors: IJobHasContractor[] = await props.getJobContractors(props.work.id, props.job.id);


        const contractorByContractorId = new Map<number, IJobHasContractor>();
        jobContractors.forEach((jc: IJobHasContractor) => {
            contractorByContractorId.set(jc.contractorId, jc);
        });

      const isAncestor = (startJobHasContractorId: number, targetContractorId: number): boolean => {
            let currentId: number | undefined = startJobHasContractorId;
            const visited = new Set<number>();

            while (currentId !== undefined) {
                if (visited.has(currentId)) {
                    break;
                }
                visited.add(currentId);
                const current = jobContractors.find((jc: IJobHasContractor) => jc.id === currentId);
                if (!current) {
                    break;
                }
                if (current.contractorId === targetContractorId) {
                    return true;
                }
                if (!current.parentId) {
                    break;
                }
                currentId = current.parentId;
            }
            return false;
        };

      const filteredData = data.filter((d: any) => {
            const foundContractor = jobContractors.find((jc: IJobHasContractor) => jc.contractorId == d.id);

            if (props.parentContractor && props.parentContractor.contractorId === d.id) {
                return false;
            }
            if (props.parentContractor && isAncestor(props.parentContractor.id, d.id)) {
                return false;
            }
            if (foundContractor) {
                if (props.parentContractor) {
                    const existsUnderSameParent = jobContractors.some(
                        (jc: IJobHasContractor) => jc.contractorId == d.id && jc.parentId === props.parentContractor?.id
                    );
                    if (existsUnderSameParent) {
                        return false;
                    }
                }
            }

            return true;
        });

        data = filteredData;

        const contractorsToExclude = Array.isArray(selfContractors)
            ? selfContractors.map((c: any) => c.id)
            : selfContractors ? [selfContractors.id] : [];
        data = data
            .filter((d: IContractor) => !contractorsToExclude.includes(d.id))
            .sort((a: IContractor, b: IContractor) => a.name.localeCompare(b.name));

        const filterContractors = props.modules
        .filterDependency('job.add_main_contractor', {
            data, props, jobContractors
        });

        const filteredContractors = data.filter(filterContractors);
        setContractors(filteredContractors);
    });

    React.useEffect(() => {
        doSearch(debouncedSearchTerm);
    }, [debouncedSearchTerm]);

    React.useEffect(() => {
        setActiveIndex(selectedContractor ? 1 : 0);
    }, [selectedContractor]);


    const saveSubMainContractorRequest = loading.wrap(async (data: Partial<IJobHasContractor>) => {
        const inputData: ISubMainContractorRequest = {
            workId: props.work.id,
            jobId: props.job?.id,
            targetContractorId: data.contractorId!,
            jobsDescription: data.jobsDescription!,
            shipmentNumber: data.shipmentNumber! || '',
            startDate: data.startDate!,
        };
        const resp = await props.saveSubMainContractorRequest(props.work.id, inputData);

        if (resp.hasValue) {
            props.onSuccess('Contractor requested successfully');
            return { ok: true };
        }
        else {
            return { ok: false, message: t(resp.error) };
        }
    });



    //TODO: CHECK SAVE SUBCONTRACTOR ?

    const saveSubContractorRequest = loading.wrap(async (data: Partial<IJobHasContractor>) => {
        const inputData: ISubContractorRequest = {
            workId: props.work.id,
            jobId: props.job?.id,
            jobsDescription: data.jobsDescription!,
            shipmentNumber: data.shipmentNumber!,
            jobHasContractorId: data.parentId!,
            sourceContractorId: props.parentContractor?.id,
            targetContractorId: data.contractorId!,
            properties: data.properties,
        };
        const resp = await props.saveSubContractorRequest(props.work.id, inputData);

        if (resp.hasValue) {
            props.onSuccess('Contractor requested successfully');
            return { ok: true };
        }
        else {
            return { ok: false, message: t(resp.error) };
        }
    });

    const settings = useWorkFormSettings({work: props.work, scope: 'contractor'});

    const saveNewContractor = loading.wrap(async (data: Partial<IJobHasContractor>) => {
        if (data.startDate == null)
        {
            data.startDate = new Date;
        }
        const resp = await props.saveJobHasContractor(props.work.id, data);

        if (resp.hasValue) {
            props.onSuccess('Contractor requested successfully');
            return { ok: true };
        }
        else {
            return { ok: false, message: t(resp.error) };
        }
    });

    const modulePermissionProps = {
        security: props.security,
        moduleManager: props.modules,
        work: props.work,
    }

    const permissionCtx = {
        ...props
    };

    const modulePermissions = usePermissions(
        modulePermissionProps,
        {
            debug: true,
            ctx: permissionCtx,
        }, {
            canAddContractorBeingMain: {
                name: 'jobs.add.contractor.main',
                default: false,
        },
    });

    const form = useForm<Partial<IJobHasContractor>>({
        initialValues: {
            jobId: props.job.id,
            parentId: props.parentContractor?.id,
            contractorId: selectedContractor?.id,
            properties: [],
        },
        validateOnMount: true,
        validate: (data: Partial<IJobHasContractor>) => {
            return validateContractor(data, props.work);
        },
        onSubmit: (data: Partial<IJobHasContractor>) => {
            data.contractorId = selectedContractor?.id;
            const canAddContractorBeingMain = modulePermissions.get('canAddContractorBeingMain');
            if (props.main ===false) {
                return saveSubMainContractorRequest(data);
            }
            else if (props.security.isContractor() && !canAddContractorBeingMain) {
                return saveSubContractorRequest(data);
            }
            else {
                return saveNewContractor(data);
            }
        },
    });

    const contractorsTable = useDataTable({
        columns: [
            'code',
            'name',
            ...props.modules.injectDataColumns('jobHasContractors.add.list', {workId: props.work.id}),
        ],
        actions: [
            {text: 'Select', headerStyle: {width: '116px'}, className: 'alt slim', onClick: setSelectedContractor},
        ],
        data: contractors,
        className: 'center-column'
    });

    const noShipmentNumber = settings.show('shipmentNumber') && props.parentContractor == undefined;

    return <>
        {!props.parentContractor &&
            <BreadcrumbItem
                text={t('Add contractor')} />}

        {props.parentContractor &&
            <BreadcrumbItem>
                {t('Add sub contractor')}
                <Chip className='sm mr-left primary' label={props.parentContractor.contractor.name} />
            </BreadcrumbItem>}

        <Accordion
            className='flat borderless'
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}>
            <AccordionTab header={t('Search contractor')}>
                <div className='c'>
                    <div className='r'>
                        <input
                            className='e'
                            type='search'
                            value={currentSearchTerm.value}
                            onChange={currentSearchTerm.set} />
                        {loading.render()}
                    </div>
                    {contractorsTable()}
                </div>
            </AccordionTab>
            <AccordionTab header={t('Contractor data')} disabled={!selectedContractor}>
                {selectedContractor &&
                    <form
                        className='sm pd c'
                        onSubmit={form.handleSubmit}>
                        <div className='r'>
                            <div className='c e form-2'>
                                <F label={t('Contractor to add')}>
                                    <span>
                                        {selectedContractor?.name}
                                        <Chip label={selectedContractor?.code} className='sm mr-left'/>
                                    </span>
                                </F>
                                <F label={t("Work's entry date")}>
                                    {form.input('startDate', { type: 'date'})}
                                </F>
                                <F label={t("Jobs into this work")}>
                                    {form.textarea('jobsDescription')}
                                </F>
                                {/** Solo mostramos el numero de pedido en los contratistas
                                 * principales
                                 */}
                                {noShipmentNumber &&
                                <F label={t("Shipment number")}>
                                    {form.textarea('shipmentNumber')}
                                </F>}
                            </div>

                            <div className='c sm pd e'>
                                <DynamicProperties
                                    propertyGroupTypes={props.propertyGroupTypes}
                                    object={form.values}
                                    objectType={PropertyGroupObjectType.JobHasContractor}
                                    onChange={(properties: IPropertyGroup[]) => form.setFieldValue('properties', properties)} />
                            </div>
                        </div>
                        <div className='e'></div>

                        <div className='errors-container'>
                            {form.errorBox()}
                        </div>
                        {(props.work.contractorSettings?.jhcStartDateMandatory == true) &&
                            <div>
                                {!(dateUtils.compareDates(dateUtils.parseDate(form.values.startDate), dateUtils.parseDate(props.job.startDate as any)) >= 0) &&
                                    <small className='p-error right-align'>
                                        {t('La fecha no puede ser anterior a la fecha de inicio de contrato')}
                                    </small> ||
                                !(dateUtils.dateISTodayOrFuture(dateUtils.parseDate(form.values.startDate))) &&
                                    <small className='p-error right-align'>
                                        {t('La fecha no puede ser anterior a la del día de hoy')}
                                    </small> ||
                                !(dateUtils.compareDates(dateUtils.parseDate(form.values.startDate), dateUtils.parseDate(props.job.endDate as any)) < 0) &&
                                    <small className='p-error right-align'>
                                        {t('La fecha no puede ser mayor a la fecha de fin de contrato')}
                                    </small>
                                }
                            </div>
                        }
                        <div className='cols-2 r'>
                            <span className='e'></span>
                            {loading.render()}
                            <div className='p-buttonset'>
                                <button
                                    onClick={() => props.onCancel()}>
                                    {t('Cancel')}
                                </button>
                                {props.work.contractorSettings?.jhcStartDateMandatory == true &&
                                    <button
                                        disabled={(!isValid(form.values, props.work) || loading.isLoading() ||
                                            !(dateUtils.compareDates(dateUtils.parseDate(form.values.startDate), dateUtils.parseDate(props.job.startDate as any)) >= 0) ||
                                            !(dateUtils.compareDates(dateUtils.parseDate(form.values.startDate), dateUtils.parseDate(props.job.endDate as any)) < 0) ||
                                            !(dateUtils.dateISTodayOrFuture(dateUtils.parseDate(form.values.startDate))))
                                        }
                                        type='submit'
                                        className='primary'>
                                        {t('Create')}
                                    </button>
                                }
                                {props.work.contractorSettings?.jhcStartDateMandatory == false &&
                                    <button
                                        type='submit'
                                        className='primary'>
                                        {t('Create')}
                                    </button>
                                }
                            </div>
                        </div>
                    </form>}
            </AccordionTab>
        </Accordion>
    </>;
}

export function JobAddContractor(props: IProps) {
    const ctx = {
        Accordion: Accordion,
        AccordionTab: AccordionTab,
        BreadcrumbItem: BreadcrumbItem,
        Chip: Chip,
        F: F,
        useDataTable: useDataTable,
        useDebounce: useDebounce,
        useForm: useForm,
        useLoading: useLoading,
        usePermissions: usePermissions,
        useSearch: useSearch,
        useStringState: useStringState,
        useTranslation: useTranslation,
        useWorkFormSettings: useWorkFormSettings,
        ValidationBuilder: ValidationBuilder,
        DynamicProperties: DynamicProperties,
        dateUtils: dateUtils,
    };

    const res = props.modules.renderComponent<IProps>('JobAddContractor', { ...props, ctx }, JobAddContractorImpl);
    return res;
}
